import { useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import withCx, { CxProps } from 'fe-core/util/withCx';
import Box from 'fe-design-base/atoms/Box';
import Icon, { IconName } from 'fe-design-base/atoms/Icon';
import Badge from 'fe-design-base/molecules/Badge';
import { BadgeProps } from 'fe-design-base/molecules/Badge/Badge';

import { EVENT_ACTIONS, TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useTrackUx } from 'util/uxEvents';

export const ICON_BUTTON_SIZES = ['xsmall', 'small', 'medium'] as const;
type Size = (typeof ICON_BUTTON_SIZES)[number];

interface IconButtonProps {
  /** @param {string} label - Accessible label for the icon button.
   * Used as the aria-label attribute to provide
   * screen readers with a description of the button's purpose.
   * Avoid setting to an empty string as it will cause
   * the button to be ignored by screen readers. */
  label: string;
  children?: React.ReactNode;
  dataTestId?: string;
  destructive?: boolean;
  disabled?: boolean;
  icon: IconName;
  id?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  size?: Size;
  uxElement?: string;
  badgeProps?: Omit<BadgeProps, 'children'>;
}

const getMUIButtonSize = (size: Size) => {
  // MUI only supports "small", "medium", and "large"
  // https://mui.com/material-ui/api/icon-button/#icon-button-prop-size
  if (size === 'xsmall' || size === 'small') return 'small';
  return 'medium';
};

const getIconBoxDimensions = (size: Size) => {
  if (size === 'xsmall') return { w: '28px', h: '28px' };
  if (size === 'small') return { w: '32px', h: '32px' };
  return { w: '36px', h: '36px' };
};

const ButtonWithIcon = ({
  cx,
  dataTestId = 'FDBIconButton',
  destructive,
  disabled,
  id,
  onClick,
  size = 'medium',
  icon,
  uxElement,
  badgeProps,
  label,
}: IconButtonProps & CxProps) => {
  const trackUx = useTrackUx({
    element: uxElement,
    icon,
    size,
  } as any);
  const { w, h } = getIconBoxDimensions(size);

  // Keeping this fallback and the null check below for IconButtons with missing labels in JSX files
  let ariaLabel = label || '';

  if (badgeProps?.text) {
    ariaLabel = ariaLabel?.length
      ? `${label} (${badgeProps.text})`
      : `${badgeProps.text}`;
  }

  const handleClick = useCallback(
    // TODO: DB https://joinhomebase.atlassian.net/browse/FE-2199
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    e => {
      if (uxElement) {
        trackUx(EVENT_ACTIONS.ICON_BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK);
      }
      onClick?.(e);
    },
    [onClick, trackUx, uxElement]
  );

  return (
    <IconButton
      id={id}
      className={cx({ destructive })}
      data-testid={dataTestId}
      disableRipple
      onClick={handleClick}
      disabled={disabled}
      size={getMUIButtonSize(size)}
      aria-label={ariaLabel?.length ? ariaLabel : undefined}
    >
      <Box w={w} h={h} relative flex alignItemsCenter justifyContentCenter>
        {badgeProps ? (
          <Badge
            text={badgeProps.text}
            disabled={disabled}
            variant={badgeProps.variant}
            borderColor={badgeProps.borderColor}
          >
            <Icon size={size} iconName={icon} />
          </Badge>
        ) : (
          <Icon size={size} iconName={icon} />
        )}
      </Box>
    </IconButton>
  );
};

export default withCx<IconButtonProps>('FDBIconButton')(ButtonWithIcon);
